import React from "react";

/**
 * Schiscetta logo — SVG custom (no emoji).
 * Represents: bento box / lunch container with two compartments (one rice, one main),
 * a small steam wisp, set on a wordmark-friendly square footprint.
 *
 * Use:
 *   <Logo size={28} />            // mark only, square
 *   <Logo size={28} withWordmark /> // mark + "Schiscetta"
 */
export default function Logo({ size = 28, withWordmark = false, className = "", color = "#557555", accent = "#b8462e" }) {
  const id = React.useId();
  return (
    <span className={`inline-flex items-center gap-2 align-middle ${className}`} aria-label="Schiscetta">
      <svg
        width={size}
        height={size}
        viewBox="0 0 40 40"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-hidden={withWordmark}
        style={{ display: "block" }}
      >
        <defs>
          <linearGradient id={`${id}-bg`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={color} />
            <stop offset="100%" stopColor={shade(color, -10)} />
          </linearGradient>
        </defs>
        {/* Rounded square container (bento box) */}
        <rect x="3" y="9" width="34" height="26" rx="5" ry="5" fill={`url(#${id}-bg)`} />
        {/* Lid line: horizontal divider */}
        <rect x="3" y="13" width="34" height="2" fill={shade(color, -25)} opacity="0.55" />
        {/* Left compartment — rice grains (3 small dots) */}
        <circle cx="12" cy="22" r="1.3" fill="#fff" opacity="0.95" />
        <circle cx="15.5" cy="24.5" r="1.3" fill="#fff" opacity="0.85" />
        <circle cx="12" cy="27" r="1.3" fill="#fff" opacity="0.75" />
        {/* Vertical divider between compartments */}
        <rect x="19.4" y="16" width="1.2" height="16" rx="0.6" fill={shade(color, -25)} opacity="0.55" />
        {/* Right compartment — main dish (accent half-circle + dot) */}
        <path
          d="M 28 28 a 4 4 0 0 1 -8 0 Z"
          fill={accent}
          opacity="0.95"
        />
        <circle cx="29" cy="22" r="1.4" fill="#fff" opacity="0.8" />
        {/* Steam wisp on top */}
        <path
          d="M 17 8 q 1.5 -2.5 0 -5 q -1.5 -2.5 0 -5"
          fill="none"
          stroke={shade(color, -10)}
          strokeWidth="1.4"
          strokeLinecap="round"
          opacity="0.7"
          transform="translate(0 6)"
        />
        <path
          d="M 22 8 q 1.5 -2.5 0 -5 q -1.5 -2.5 0 -5"
          fill="none"
          stroke={shade(color, -10)}
          strokeWidth="1.4"
          strokeLinecap="round"
          opacity="0.45"
          transform="translate(0 6)"
        />
      </svg>
      {withWordmark && (
        <span className="font-display font-semibold text-lg tracking-tight">Schiscetta</span>
      )}
    </span>
  );
}

// Tiny helper to darken/lighten a hex color (no external deps).
function shade(hex, percent) {
  const n = parseInt(hex.replace("#", ""), 16);
  let r = (n >> 16) & 255;
  let g = (n >> 8) & 255;
  let b = n & 255;
  const f = (c) => Math.max(0, Math.min(255, Math.round(c + (c * percent) / 100)));
  r = f(r); g = f(g); b = f(b);
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}
